import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button, CameraCaptureView, CategoryChip, ResultCelebration, RewardPill, ScannerFrame } from "@ec2/ui";
import { useT } from "@ec2/i18n/react";
import type { CompletionView } from "@ec2/contracts";
import { useTRPC } from "../lib/api";
import { pollCompletion } from "../lib/completion";
import { generalError, type LocalizedError } from "../lib/errors";
import { CATEGORY_LABEL_KEY, flattenQuests, uiCategory } from "../lib/quests";
import { applyZoom, zoomSupportOf, type ZoomSupport } from "../lib/camera";
import { postWithProgress } from "../lib/upload";
import { useActive, usePasskeyPrompt, useQuestList } from "../state/hooks";
import { useSession } from "../state/session";
import { Shell } from "../components/Shell";
import { GroupBonusView } from "../components/GroupBonusView";
import { PasskeyPrompt } from "../components/PasskeyPrompt";
import { ErrorLine, LoadingLine, ScreenHeader } from "../components/common";

type Beat = "brief" | "capture" | "preview" | "uploading" | "processing" | "result";

/** Live camera for the capture beat; falls back to `<input capture>` when unavailable. */
function useCaptureCamera(enabled: boolean, facing: "environment" | "user") {
  const videoRef = useRef<HTMLVideoElement>(null);
  const trackRef = useRef<MediaStreamTrack | null>(null);
  const [failed, setFailed] = useState(false);
  const [zoom, setZoomState] = useState<ZoomSupport | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let stream: MediaStream | null = null;
    let cancelled = false;
    const start = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) throw new Error("no camera");
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing } });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }
        const track = stream.getVideoTracks()[0] ?? null;
        trackRef.current = track;
        setZoomState(zoomSupportOf(track));
      } catch {
        if (!cancelled) setFailed(true);
      }
    };
    void start();
    return () => {
      cancelled = true;
      setZoomState(null);
      trackRef.current = null;
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, [enabled, facing]);

  const setZoom = useCallback((level: number) => {
    const track = trackRef.current;
    if (!track) return;
    void applyZoom(track, level).then((ok) => {
      if (ok) setZoomState((z) => (z ? { ...z, current: level } : z));
    });
  }, []);

  const capture = useCallback(async (): Promise<Blob | null> => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0) return null;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")?.drawImage(video, 0, 0);
    return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.9));
  }, []);

  return { videoRef, capture, failed, zoom, setZoom };
}

/**
 * Photo-quest flow (brief §6, five beats): brief → capture → preview →
 * AI processing (non-blocking poll) → result with the AI's verdict.
 */
export function PhotoQuestScreen({ questId }: { questId: string }) {
  const t = useT();
  const navigate = useNavigate();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { api } = useSession();
  const { active } = useActive();
  const quests = useQuestList(active?.eventId);
  const quest = quests.data ? flattenQuests(quests.data).find((q) => q.id === questId) : undefined;

  const [beat, setBeat] = useState<Beat>("brief");
  const [keepPhoto, setKeepPhoto] = useState(false);
  const [facing, setFacing] = useState<"environment" | "user">("environment");
  const [photo, setPhoto] = useState<{ blob: Blob; url: string } | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<LocalizedError | null>(null);
  const [completion, setCompletion] = useState<CompletionView | null>(null);
  // §5: one celebration at a time — the group bonus follows the photo's own.
  const [showGroupBonus, setShowGroupBonus] = useState(false);
  // Two pickers on purpose: `capture` makes the OS open its camera, so the
  // gallery button must use an input WITHOUT it — that was the "choose from
  // gallery opens a camera" bug. The capture input is only the fallback for
  // when the live camera is unavailable.
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const libraryInputRef = useRef<HTMLInputElement>(null);
  // A rejected earlier try is shown first (reason + try again / back) until dismissed.
  const [reviewingFailure, setReviewingFailure] = useState(true);
  const camera = useCaptureCamera(beat === "capture", facing);
  const passkey = usePasskeyPrompt();
  const [showPasskey, setShowPasskey] = useState(false);

  const uploadUrl = useMutation(trpc.quest.photoUploadUrl.mutationOptions());
  const submit = useMutation(trpc.quest.submitPhoto.mutationOptions());

  // Judging normally takes ~30 s. Past a minute, say so: it may still come
  // (push), and the stale-photo sweep frees the quest again after 30 min.
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (beat !== "processing") return;
    setSlow(false);
    const timer = setTimeout(() => setSlow(true), 60_000);
    return () => clearTimeout(timer);
  }, [beat]);

  // Back on a quest whose photo is still being judged: keep polling, so the
  // verdict (or the sweep's "send it again") shows up without a reload.
  const pendingOnOpen = beat === "brief" && quest?.completion?.status === "processing";
  useEffect(() => {
    if (!pendingOnOpen) return;
    const abort = new AbortController();
    void pollCompletion(api, questId, { signal: abort.signal, maxWaitMs: 10 * 60_000 }).then((final) => {
      if (!abort.signal.aborted && final && final.status !== "processing") void queryClient.invalidateQueries();
    });
    return () => abort.abort();
  }, [pendingOnOpen, api, questId, queryClient]);

  // Camera unavailable in the capture beat → native file capture instead.
  useEffect(() => {
    if (beat === "capture" && camera.failed) cameraInputRef.current?.click();
  }, [beat, camera.failed]);

  const takePhoto = async () => {
    const blob = await camera.capture();
    if (!blob) {
      cameraInputRef.current?.click();
      return;
    }
    setPhoto({ blob, url: URL.createObjectURL(blob) });
    setBeat("preview");
  };

  const onFilePicked = (file: File | undefined) => {
    if (!file) return;
    setPhoto({ blob: file, url: URL.createObjectURL(file) });
    setBeat("preview");
  };

  const upload = async () => {
    if (!photo) return;
    setBeat("uploading");
    setError(null);
    try {
      const contentType = photo.blob.type || "image/jpeg";
      const { url, fields, key } = await uploadUrl.mutateAsync({ questId, contentType });
      await postWithProgress(url, fields, photo.blob, setProgress);
      await submit.mutateAsync({ questId, photoKey: key, keepPhoto });
      setBeat("processing");
      const final = await pollCompletion(api, questId);
      setCompletion(final);
      await queryClient.invalidateQueries();
      if (final?.status === "completed" && passkey.shouldPrompt) {
        passkey.markPrompted();
        setShowPasskey(true);
      }
      if (final && final.status !== "processing") setBeat("result");
    } catch (err) {
      setError(generalError(err));
      setBeat("preview");
    }
  };

  if (!quest) {
    return (
      <Shell active="quests">{quests.isLoading ? <LoadingLine /> : <ScreenHeader title={t("ec2.error.notFound")} />}</Shell>
    );
  }

  const category = uiCategory(quest.category);
  const leaveCamera = () => {
    setPhoto(null);
    setError(null);
    setBeat("brief");
  };
  const triesLabel =
    quest.retriesLeft > 0 ? t("ec2.photo.triesLeft", { count: quest.retriesLeft }) : undefined;
  const pickers = (
    <>
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        aria-label={t("ec2.camera.shutter")}
        data-testid="ec-photo-input"
        onChange={(e) => onFilePicked(e.target.files?.[0])}
      />
      <input
        ref={libraryInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        aria-label={t("ec2.photo.chooseLibrary")}
        data-testid="ec-photo-library-input"
        onChange={(e) => onFilePicked(e.target.files?.[0])}
      />
    </>
  );
  const wallAvailable = quest.enablePhotoWall === true;

  const wallToggle = (
    <label className="flex items-center gap-2 px-6 text-[13px] font-medium text-white">
      <input
        type="checkbox"
        checked={keepPhoto}
        onChange={(e) => setKeepPhoto(e.target.checked)}
        data-testid="ec-wall-toggle"
        className="size-4 accent-(--primary)"
      />
      {t("ec2.camera.photoWall")}
    </label>
  );

  // Beats 2–3 are full takeovers (no bottom nav).
  if (beat === "capture" || beat === "preview") {
    return (
      <>
        <CameraCaptureView
          mode={beat}
          labels={{
            shutter: t("ec2.camera.shutter"),
            usePhoto: t("ec2.camera.usePhoto"),
            retake: t("ec2.camera.retake"),
            flip: t("ec2.camera.flip"),
            torch: t("ec2.scanner.torch"),
            close: t("ec2.camera.close"),
          }}
          onClose={leaveCamera}
          media={
            beat === "capture" ? (
              <video ref={camera.videoRef} playsInline muted data-testid="ec-camera-video" />
            ) : (
              <img src={photo?.url} alt="" data-testid="ec-photo-preview" />
            )
          }
          onShutter={() => void takePhoto()}
          onConfirm={() => void upload()}
          onRetake={() => {
            setPhoto(null);
            setBeat("capture");
          }}
          onFlip={() => setFacing((f) => (f === "environment" ? "user" : "environment"))}
          zoomLevels={camera.zoom?.levels}
          zoom={camera.zoom?.current}
          onZoom={camera.setZoom}
          frameGuide
          footerExtra={
            <>
              {wallAvailable && beat === "preview" && wallToggle}
              {error && <ErrorLine error={error} className="px-6 text-white" />}
            </>
          }
        />
        {pickers}
      </>
    );
  }

  // Back on a quest whose last photo was rejected: say why, and offer another
  // try only when the quest allows one.
  const lastFailure = quest.completion?.status === "failed" ? quest.completion : null;
  if (beat === "brief" && lastFailure && reviewingFailure) {
    return (
      <Shell active="quests">
        <ScreenHeader title={quest.name} />
        <div className="flex flex-col gap-4 px-4" data-testid="ec-photo-flow" data-beat="failed">
          <ResultCelebration
            status="failed"
            title={t("ec2.celebration.failedTitle")}
            message={lastFailure.aiExplanation ?? lastFailure.failureReason ?? t("ec2.photo.failedGeneric")}
            actionLabel={quest.retriesLeft > 0 ? t("ec2.celebration.tryAgain") : t("ec2.celebration.backToQuests")}
            onAction={() => {
              if (quest.retriesLeft > 0) setReviewingFailure(false);
              else void navigate({ to: "/quests" });
            }}
          />
          {triesLabel && (
            <p className="text-center text-sm text-fg-secondary" data-testid="ec-photo-tries-left">
              {triesLabel}
            </p>
          )}
        </div>
      </Shell>
    );
  }

  return (
    <Shell active="quests">
      <ScreenHeader title={quest.name} />
      <div className="flex flex-col gap-4 px-4" data-testid="ec-photo-flow" data-beat={beat}>
        {beat === "brief" && !pendingOnOpen && (
          <>
            <div className="flex items-center gap-3">
              <CategoryChip category={category} label={t(CATEGORY_LABEL_KEY[category])} />
              <span className="ml-auto">
                <RewardPill amount={quest.bucksReward} label={t("ec2.a11y.bucksReward", { amount: quest.bucksReward })} />
              </span>
            </div>
            <p className="max-w-[65ch] text-base text-fg">{quest.description}</p>
            {wallAvailable && (
              <label className="flex items-center gap-2 text-[13px] font-medium text-fg-secondary">
                <input
                  type="checkbox"
                  checked={keepPhoto}
                  onChange={(e) => setKeepPhoto(e.target.checked)}
                  data-testid="ec-wall-toggle-brief"
                  className="size-4 accent-(--primary)"
                />
                {t("ec2.camera.photoWall")}
              </label>
            )}
            <Button variant="primary" onClick={() => setBeat("capture")} data-testid="ec-photo-start">
              {t("ec2.camera.shutter")}
            </Button>
            <Button variant="ghost" onClick={() => libraryInputRef.current?.click()} data-testid="ec-photo-library">
              {t("ec2.photo.chooseLibrary")}
            </Button>
          </>
        )}

        {beat === "uploading" && (
          <div className="flex flex-col gap-3" role="status">
            <p className="text-base text-fg">{t("ec2.photo.uploading", { percent: Math.round(progress * 100) })}</p>
            <div className="h-2 overflow-hidden rounded-full bg-surface-overlay">
              <div className="h-full rounded-full bg-accent transition-[width]" style={{ width: `${progress * 100}%` }} />
            </div>
          </div>
        )}

        {(beat === "processing" || pendingOnOpen) && (
          <div className="flex flex-col items-center gap-4 py-6 text-center" role="status" data-testid="ec-photo-processing">
            <div className="relative">
              {photo && (
                <img src={photo.url} alt="" width={144} height={192} className="rounded-(--radius-card) object-cover" />
              )}
              <span className="absolute -inset-3 flex items-center justify-center">
                <ScannerFrame size={180} state="idle" />
              </span>
            </div>
            <p className="max-w-[36ch] text-base text-fg-secondary" data-testid="ec-photo-processing-caption">
              {pendingOnOpen
                ? t("ec2.photo.stillChecking")
                : slow
                  ? t("ec2.photo.slow")
                  : t("ec2.quest.processing.caption")}
            </p>
            <Button variant="ghost" onClick={() => void navigate({ to: "/quests" })} data-testid="ec-photo-dismiss">
              {t("ec2.celebration.dismissProcessing")}
            </Button>
          </div>
        )}

        {beat === "result" && completion && showGroupBonus && completion.groupBonus && (
          <GroupBonusView
            bonus={completion.groupBonus}
            onDone={() => void navigate({ to: "/quests" })}
          />
        )}
        {beat === "result" && completion && !(showGroupBonus && completion.groupBonus) && (
          <ResultCelebration
            status={completion.status === "completed" ? "solved" : "failed"}
            title={completion.status === "completed" ? t("ec2.celebration.solvedToast") : t("ec2.celebration.failedTitle")}
            amount={completion.status === "completed" ? quest.bucksReward : undefined}
            amountLabel={t("ec2.celebration.earned", { amount: quest.bucksReward })}
            message={
              completion.status === "completed"
                ? (completion.aiExplanation ?? undefined)
                : (completion.aiExplanation ?? completion.failureReason ?? t("ec2.photo.failedGeneric"))
            }
            actionLabel={
              completion.status !== "completed" && quest.retriesLeft > 0
                ? t("ec2.celebration.tryAgain")
                : t("ec2.celebration.backToQuests")
            }
            onAction={() => {
              if (completion.status === "completed") {
                if (completion.groupBonus) {
                  setShowGroupBonus(true);
                  return;
                }
                void navigate({ to: "/quests" });
              } else if (quest.retriesLeft > 0) {
                setPhoto(null);
                setCompletion(null);
                setReviewingFailure(false);
                setBeat("brief");
              } else {
                void navigate({ to: "/quests" });
              }
            }}
          />
        )}
        {beat === "result" && completion && completion.status !== "completed" && triesLabel && (
          <p className="text-center text-sm text-fg-secondary" data-testid="ec-photo-tries-left">
            {triesLabel}
          </p>
        )}
        <ErrorLine error={beat === "brief" ? error : null} />
      </div>
      {pickers}
      {showPasskey && <PasskeyPrompt onDone={() => setShowPasskey(false)} />}
    </Shell>
  );
}
